import React, { useState } from "react";
import { planTypeLabel } from "../../../utils/planType";
import {
  Card,
  Collapse,
  Descriptions,
  Tag,
  Empty,
  Spin,
  Alert,
  Row,
  Col,
  Button,
} from "antd";
import {
  HistoryOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  FileProtectOutlined,
  LinkOutlined,
  SyncOutlined,
} from "@ant-design/icons";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, decimals = 0) =>
  Number(n ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const paymentModeLabel = (mode) => {
  if (!mode) return "N/A";
  if (mode === "upi") return "📱 UPI";
  if (mode === "bank_transfer") return "🏦 Bank Transfer";
  if (mode === "cash") return "💵 Cash";
  return mode;
};

// =============================================================================
// SubscriptionHistory
//
// Props:
//   historyData  — array of subscription_logs from get-subscription-details API
//   isLoading    — boolean
//   error        — error object if fetch failed
//   allFeatures  — kept for backward compat, not used
// =============================================================================
const SubscriptionHistory = ({
  historyData = [],
  isLoading = false,
  error = null,
  allFeatures = [],
}) => {
  const allKeys = historyData.map((log, i) => log.id ?? i);
  const [activeKeys, setActiveKeys] = useState(
    historyData.length > 0 ? [historyData[0]?.id ?? 0] : []
  );
  const allExpanded = activeKeys.length === historyData.length && historyData.length > 0;

  const toggleAll = () => {
    setActiveKeys(allExpanded ? [] : allKeys);
  };

  // console.log("from history-data", historyData);
  
  if (isLoading) {
    return (
      <Card className="shadow-sm mb-4" style={{ borderRadius: 12 }}>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 200 }}
        >
          <Spin size="large" tip="Loading subscription history..." />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm mb-4" style={{ borderRadius: 12 }}>
        <Alert
          type="error"
          showIcon
          message="Failed to load subscription history"
          description={
            error?.message ||
            "An error occurred while fetching subscription history."
          }
        />
      </Card>
    );
  }

  if (!historyData || historyData.length === 0) {
    return (
      <Card className="shadow-sm mb-4" style={{ borderRadius: 12 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-muted">
              No subscription history found for this user.
            </span>
          }
        />
      </Card>
    );
  }

  // ── Build collapse items ──────────────────────────────────────────────────
  const collapseItems = historyData.map((log, index) => {
    const isPaid =
      log.payment_status === "1" ||
      log.payment_status === 1 ||
      log.payment_status === "paid";
    const isRenewed = log.is_renewed === 1;

    const headerLabel = (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <CreditCardOutlined style={{ color: "#764ba2" }} />
        <strong style={{ fontSize: 14 }}>
          {log.plan_name || `Transaction #${index + 1}`}
        </strong>
        <Tag
          color={isPaid ? "success" : "error"}
          icon={
            isPaid ? <CheckCircleOutlined /> : <CloseCircleOutlined />
          }
          style={{ fontSize: 11 }}
        >
          {isPaid ? "PAID" : "UNPAID"}
        </Tag>
        {isRenewed && (
          <Tag
            color="orange"
            icon={<SyncOutlined />}
            style={{ fontSize: 11 }}
          >
            RENEWAL
          </Tag>
        )}
        <Tag
          color="geekblue"
          style={{ fontSize: 11, textTransform: "capitalize" }}
        >
          {planTypeLabel(log.plan_type)}
        </Tag>
        <span
          style={{ fontSize: 12, color: "#8c8c8c", marginLeft: "auto" }}
        >
          <CalendarOutlined className="me-1" />
          {log.transaction_at || "N/A"}
        </span>
      </div>
    );

    return {
      key: log.id ?? index,
      label: headerLabel,
      children: (
        <div>
          {/* ── Transaction Details ──────────────────────────────────── */}
          <Descriptions
            column={{ xs: 1, sm: 2, md: 2, lg: 2 }}
            bordered
            size="small"
            className="mb-3"
          >
            <Descriptions.Item label="Invoice ID">
              {log.invoice_id ? (
                <span
                  style={{
                    fontFamily: "monospace",
                    color: "#1677ff",
                    fontWeight: 600,
                  }}
                >
                  {log.invoice_id}
                </span>
              ) : (
                "N/A"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Plan Name">
              <strong>{log.plan_name || "N/A"}</strong>
            </Descriptions.Item>

            <Descriptions.Item label="Plan Type">
              <span className="text-capitalize">
                {planTypeLabel(log.plan_type)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Plan Price">
              <strong className="text-primary">
                ₹{fmt(log.plan_price, 2)}
              </strong>
            </Descriptions.Item>

            <Descriptions.Item label="Total TM Count">
              <span
                style={{
                  display: "inline-block",
                  minWidth: 48,
                  padding: "1px 10px",
                  borderRadius: 12,
                  background: "#52c41a",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                {fmt(log.total_tm_count)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Paid Amount">
              <strong className="text-success" style={{ fontSize: 15 }}>
                ₹{fmt(log.paid_amount, 2)}
              </strong>
            </Descriptions.Item>

            <Descriptions.Item label="Transaction Date">
              {log.transaction_at || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Payment Status">
              <Tag
                icon={
                  isPaid ? (
                    <CheckCircleOutlined />
                  ) : (
                    <CloseCircleOutlined />
                  )
                }
                color={isPaid ? "success" : "error"}
              >
                {isPaid ? "Paid" : "Unpaid"}
              </Tag>
            </Descriptions.Item>

            {log.payment_mode && (
              <Descriptions.Item label="Payment Mode">
                <span className="text-capitalize">
                  {paymentModeLabel(log.payment_mode)}
                </span>
              </Descriptions.Item>
            )}

            {log.payment_mode === "bank_transfer" && log.bank_name && (
              <Descriptions.Item label="Bank">
                {log.bank_name}
              </Descriptions.Item>
            )}

            {log.utr && (
              <Descriptions.Item label="UTR / Ref No.">
                <span
                  style={{
                    fontFamily: "monospace",
                    color: "#1677ff",
                    fontWeight: 600,
                  }}
                >
                  {log.utr}
                </span>
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* ── Links ─────────────────────────────────────────────────── */}
          {(log.invoice_url || log.payment_proof_url) && (
            <div className="d-flex gap-2 flex-wrap">
              {log.invoice_url && log.plan_type !== "free_trial" && (
                <Button
                  type="default"
                  size="small"
                  icon={<FileProtectOutlined />}
                  href={log.invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Invoice
                </Button>
              )}
              {log.payment_proof_url && (
                <Button
                  type="default"
                  size="small"
                  icon={<LinkOutlined />}
                  href={log.payment_proof_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Payment Proof
                </Button>
              )}
            </div>
          )}
        </div>
      ),
    };
  });

  // ── Stats ─────────────────────────────────────────────────────────────────
  const paidLogs = historyData.filter(
    (l) =>
      l.payment_status === "1" ||
      l.payment_status === 1 ||
      l.payment_status === "paid",
  );
  const totalPaidAmount = paidLogs.reduce(
    (s, l) => s + parseFloat(l.paid_amount ?? 0),
    0,
  );

  // console.log("history:", historyData);

  return (
    <Card
      title={
        <div className="d-flex align-items-center gap-2">
          <HistoryOutlined style={{ color: "#1677ff", fontSize: 18 }} />
          <span style={{ fontWeight: 600 }}>Subscription History</span>
          <Tag color="blue" style={{ marginLeft: 4 }}>
            {historyData.length}
          </Tag>
        </div>
      }
      extra={
        <Button size="small" onClick={toggleAll}>
          {allExpanded ? "Collapse All" : "Expand All"}
        </Button>
      }
      className="shadow-sm mb-4"
      style={{ borderRadius: 12 }}
    >
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        items={collapseItems}
        style={{ background: "transparent", border: "none" }}
        expandIconPosition="end"
      />

      {/* Stats bar */}
      <div
        className="mt-3 p-3 rounded"
        style={{
          background: "linear-gradient(135deg,#f0f7ff 0%,#e8f4fd 100%)",
          border: "1px solid #bae0ff",
        }}
      >
        <Row className="text-center g-0">
          <Col span={8} style={{ borderRight: "1px solid #bae0ff" }}>
            <div
              style={{ fontSize: 22, fontWeight: 700, color: "#1677ff" }}
            >
              {historyData.length}
            </div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              Total Transactions
            </div>
          </Col>
          <Col span={8} style={{ borderRight: "1px solid #bae0ff" }}>
            <div
              style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}
            >
              {paidLogs.length}
            </div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>Paid</div>
          </Col>
          <Col span={8}>
            <div
              style={{ fontSize: 20, fontWeight: 700, color: "#722ed1" }}
            >
              ₹{fmt(totalPaidAmount, 2)}
            </div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              Total Paid
            </div>
          </Col>
        </Row>
      </div>

      <style>{`
        .ant-collapse > .ant-collapse-item {
          border-radius: 10px !important;
          margin-bottom: 10px;
          border: 1px solid #f0f0f0 !important;
          overflow: hidden;
        }
        .ant-collapse > .ant-collapse-item > .ant-collapse-header {
          padding: 14px 16px !important;
          background: #fafafa;
        }
        .ant-collapse > .ant-collapse-item-active > .ant-collapse-header {
          background: #f0f7ff;
        }
        .ant-collapse > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
          padding: 16px !important;
        }
      `}</style>
    </Card>
  );
};

export default SubscriptionHistory;
